/**
 * Locale Router Components
 *
 * Provides locale-aware routing primitives:
 * - LocaleRedirect: Redirects bare "/" to "/:locale" based on detected language
 * - LocaleSync: Syncs URL locale with i18n state on every route change
 *
 * These are used in App.tsx to wrap the main route tree.
 */

import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  applyLocaleToDocument,
  type SupportedLocale,
} from './index';

/**
 * Redirects the root path "/" to "/:locale/" based on the detected language.
 * The i18n language detector has already determined the user's language
 * from localStorage or navigator.languages by the time this renders.
 */
export const LocaleRedirect: React.FC = () => {
  const { i18n } = useTranslation();

  // Use the already-detected language from i18next
  const detectedLocale = SUPPORTED_LOCALES.includes(i18n.language as SupportedLocale)
    ? i18n.language
    : DEFAULT_LOCALE;

  return <Navigate to={`/${detectedLocale}`} replace />;
};

/**
 * Syncs the URL's :locale parameter with i18next's active language.
 * Renders as a wrapper inside the /:locale route -- detects locale changes
 * from URL navigation (e.g., clicking a /ar/... link) and updates i18n.
 *
 * Also validates that the locale in the URL is supported.
 */
/**
 * Layout route component that syncs URL locale with i18n.
 * Renders <Outlet /> to allow nested child routes.
 * Used as: <Route path="/:locale" element={<LocaleSync />}>
 */
export const LocaleSync: React.FC = () => {
  const { locale } = useParams<{ locale: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  const isValidLocale = !!locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale);

  // Sync i18n language when URL locale changes
  // Must be called before any conditional returns to satisfy Rules of Hooks
  useEffect(() => {
    if (isValidLocale && locale !== i18n.language) {
      i18n.changeLanguage(locale);
    }
    if (isValidLocale) {
      applyLocaleToDocument(locale);
    }
  }, [locale, i18n, isValidLocale]);

  // If the URL locale is not supported, prepend the user's detected locale
  // and keep the full original path intact. This handles bare URLs like
  // /verify-email or /dashboard/create-campaign from external links,
  // backend-generated URLs, and bookmarks.
  if (!isValidLocale) {
    const detectedLocale = SUPPORTED_LOCALES.includes(i18n.language as SupportedLocale)
      ? i18n.language
      : DEFAULT_LOCALE;

    const fullPath = location.pathname.startsWith('/')
      ? location.pathname.substring(1)
      : location.pathname;

    const searchAndHash = location.search + location.hash;

    return <Navigate to={`/${detectedLocale}/${fullPath}${searchAndHash}`} replace />;
  }

  return <Outlet />;
};

export default LocaleSync;
